import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// 데이터 모델 타입 정의
/**
 * @typedef {Object} Club
 * @property {string} id - 클럽 ID
 * @property {string} name - 클럽 이름
 * @property {string} description - 클럽 설명
 * @property {'public'|'private'|'secret'} visibility - 공개 범위
 * @property {number} maxMembers - 최대 멤버 수 (10)
 * @property {string} ownerUid - 소유자 UID
 * @property {Date} createdAt - 생성일
 * @property {Date} updatedAt - 수정일
 * @property {Object} settings - 설정
 * @property {boolean} settings.showHighlights - 하이라이트 표시 여부
 * @property {boolean} settings.showMemos - 메모 표시 여부
 * @property {boolean} settings.defaultShare - 기본 공유 여부
 */

/**
 * @typedef {Object} ClubMember
 * @property {string} groupId - 그룹 ID
 * @property {string} uid - 사용자 UID
 * @property {'owner'|'admin'|'member'} role - 역할
 * @property {Date} joinedAt - 가입일
 * @property {boolean} isActive - 활성 상태
 */

/**
 * @typedef {Object} ClubStats
 * @property {number} memberCount - 멤버 수
 * @property {number} currentReadingCount - 현재 읽는 중인 멤버 수
 * @property {Date} lastUpdatedAt - 마지막 업데이트
 */

/**
 * @typedef {Object} ClubNowRead
 * @property {string} uid - 사용자 UID
 * @property {string} bookId - 책 ID
 * @property {string} title - 책 제목
 * @property {string} coverUrl - 표지 URL
 * @property {'reading'|'completed'|'not_started'} readStatus - 읽기 상태
 * @property {Date} startedAt - 시작일
 * @property {Date} expectedFinishAt - 예상 완독일
 * @property {number} progressPct - 진행률 (0-100)
 * @property {Date} lastUpdatedAt - 마지막 업데이트
 */

// 공개 모임 목록 조회
export const getPublicClubs = async (limitCount = 20) => {
  try {
    const clubsQuery = query(
      collection(db, 'groups'),
      where('visibility', '==', 'public'),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(clubsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching public clubs:', error);
    
    // 인덱스 관련 오류인 경우 빈 배열 반환
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      console.warn('Firestore index not ready yet, returning empty array');
      return [];
    }
    
    throw error;
  }
};

// 모임 상세 조회
export const getClubById = async (clubId) => {
  try {
    const clubDoc = await getDoc(doc(db, 'groups', clubId));
    if (!clubDoc.exists()) {
      throw new Error('Club not found');
    }
    return {
      id: clubDoc.id,
      ...clubDoc.data()
    };
  } catch (error) {
    console.error('Error fetching club:', error);
    throw error;
  }
};

// 사용자가 속한 모임 목록 조회
export const getUserClubs = async (userId) => {
  try {
    const membersQuery = query(
      collection(db, 'groupMembers'),
      where('uid', '==', userId),
      where('isActive', '==', true)
    );
    
    const membersSnapshot = await getDocs(membersQuery);
    const clubIds = membersSnapshot.docs.map(doc => doc.data().groupId);
    
    if (clubIds.length === 0) return [];
    
    const clubs = [];
    for (const clubId of clubIds) {
      try {
        const club = await getClubById(clubId);
        clubs.push(club);
      } catch (error) {
        console.warn(`Failed to fetch club ${clubId}:`, error);
      }
    }
    
    return clubs.sort((a, b) => b.createdAt - a.createdAt);
  } catch (error) {
    console.error('Error fetching user clubs:', error);
    
    // 인덱스 관련 오류인 경우 빈 배열 반환
    if (error.code === 'failed-precondition' || error.message.includes('index')) {
      console.warn('Firestore index not ready yet, returning empty array');
      return [];
    }
    
    throw error;
  }
};

// 모임 생성
export const createClub = async (clubData) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const clubDoc = await addDoc(collection(db, 'groups'), {
      ...clubData,
      ownerUid: currentUser.uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      maxMembers: 10
    });

    // 소유자를 멤버로 추가
    await addDoc(collection(db, 'groupMembers'), {
      groupId: clubDoc.id,
      uid: currentUser.uid,
      role: 'owner',
      joinedAt: serverTimestamp(),
      isActive: true
    });

    // 통계 초기화
    await setDoc(doc(db, 'groupStats', clubDoc.id), {
      memberCount: 1,
      currentReadingCount: 0,
      lastUpdatedAt: serverTimestamp()
    });

    return clubDoc.id;
  } catch (error) {
    console.error('Error creating club:', error);
    throw error;
  }
};

// 모임 멤버 조회
export const getClubMembers = async (clubId) => {
  try {
    const membersQuery = query(
      collection(db, 'groupMembers'),
      where('groupId', '==', clubId),
      where('isActive', '==', true),
      orderBy('joinedAt', 'asc')
    );
    
    const snapshot = await getDocs(membersQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching club members:', error);
    throw error;
  }
};

// 모임 통계 조회
export const getClubStats = async (clubId) => {
  try {
    const statsDoc = await getDoc(doc(db, 'groupStats', clubId));
    if (!statsDoc.exists()) {
      return {
        memberCount: 0,
        currentReadingCount: 0,
        lastUpdatedAt: null
      };
    }
    return statsDoc.data();
  } catch (error) {
    console.error('Error fetching club stats:', error);
    throw error;
  }
};

// 모임 멤버의 현재 읽는 책 조회
export const getClubNowReads = async (clubId, limitCount = 20) => {
  try {
    const nowReadsQuery = query(
      collection(db, 'groupNowReads', clubId),
      orderBy('lastUpdatedAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(nowReadsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching club now reads:', error);
    throw error;
  }
};

// 모임에 가입
export const joinClub = async (clubId, userId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // 모임 정보 조회
    const club = await getClubById(clubId);
    
    // 정원 확인
    const stats = await getClubStats(clubId);
    if (stats.memberCount >= club.maxMembers) {
      throw new Error('Club is full');
    }

    // 이미 멤버인지 확인
    const existingMember = await getDoc(
      doc(db, 'groupMembers', `${clubId}_${currentUser.uid}`)
    );
    
    if (existingMember.exists()) {
      throw new Error('Already a member');
    }

    const batch = writeBatch(db);

    // 멤버 추가
    batch.set(doc(db, 'groupMembers', `${clubId}_${currentUser.uid}`), {
      groupId: clubId,
      uid: currentUser.uid,
      role: 'member',
      joinedAt: serverTimestamp(),
      isActive: true
    });

    // 통계 업데이트
    batch.update(doc(db, 'groupStats', clubId), {
      memberCount: stats.memberCount + 1,
      lastUpdatedAt: serverTimestamp()
    });

    await batch.commit();
  } catch (error) {
    console.error('Error joining club:', error);
    throw error;
  }
};

// 모임에서 탈퇴
export const leaveClub = async (clubId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const memberDoc = doc(db, 'groupMembers', `${clubId}_${currentUser.uid}`);
    const memberData = (await getDoc(memberDoc)).data();
    
    if (!memberData) {
      throw new Error('Not a member');
    }

    // 소유자는 탈퇴 불가
    if (memberData.role === 'owner') {
      throw new Error('Owner cannot leave club');
    }

    const batch = writeBatch(db);

    // 멤버 비활성화
    batch.update(memberDoc, {
      isActive: false,
      leftAt: serverTimestamp()
    });

    // 통계 업데이트
    const stats = await getClubStats(clubId);
    batch.update(doc(db, 'groupStats', clubId), {
      memberCount: Math.max(0, stats.memberCount - 1),
      lastUpdatedAt: serverTimestamp()
    });

    await batch.commit();
  } catch (error) {
    console.error('Error leaving club:', error);
    throw error;
  }
};

// 실시간 모임 멤버 구독
export const subscribeToClubMembers = (clubId, callback) => {
  const membersQuery = query(
    collection(db, 'groupMembers'),
    where('groupId', '==', clubId),
    where('isActive', '==', true)
  );
  
  return onSnapshot(membersQuery, (snapshot) => {
    const members = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(members);
  });
};

// 실시간 모임 현재 읽는 책 구독
export const subscribeToClubNowReads = (clubId, callback) => {
  const nowReadsQuery = query(
    collection(db, 'groupNowReads', clubId),
    orderBy('lastUpdatedAt', 'desc')
  );
  
  return onSnapshot(nowReadsQuery, (snapshot) => {
    const nowReads = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(nowReads);
  });
};
